"use client";

import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import type { ToolUIPart } from "ai";
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import type {
  ComponentProps,
  FocusEventHandler,
  ReactElement,
  ReactNode,
} from "react";
import {
  createContext,
  isValidElement,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CopyToClipboardButton } from "@/components/ui/copy-to-clipboard-button";
import { getToolDisplayMeta } from "@/lib/ai/tools/ui/display-meta";
import { cn } from "@/lib/utils";
import { CodeBlock } from "./code-block";
import { Shimmer } from "./shimmer";
import { VirtualizedCodeBlock } from "./virtualized-code-block";

export type ToolProps = ComponentProps<typeof Collapsible>;

const ToolOpenContext = createContext(true);

function useToolOpen(): boolean {
  return useContext(ToolOpenContext);
}

function ToolComponent({
  className,
  defaultOpen,
  open: openProp,
  onOpenChange,
  ...props
}: ToolProps): ReactElement {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen ?? false,
  );
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : uncontrolledOpen;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(nextOpen);
      }
      onOpenChange?.(nextOpen);
    },
    [isControlled, onOpenChange],
  );

  return (
    <ToolOpenContext.Provider value={open}>
      <Collapsible
        className={cn("not-prose mb-4 w-full rounded-md border", className)}
        data-ai-tool=""
        onOpenChange={handleOpenChange}
        open={open}
        {...props}
      />
    </ToolOpenContext.Provider>
  );
}

export const Tool = memo(
  ToolComponent,
  (prev, next) =>
    prev.className === next.className &&
    prev.children === next.children &&
    // Respect controlled/uncontrolled open state props
    // If either changes, re-render to keep Radix state in sync
    prev.open === next.open &&
    prev.defaultOpen === next.defaultOpen,
);

export type ToolHeaderProps = {
  title?: string;
  showIcon?: boolean;
  toolName?: string;
  type: ToolUIPart["type"] | "dynamic-tool";
  state: ToolUIPart["state"];
  layout?: "card" | "activity";
  shimmerTitle?: boolean;
  titleTone?: "default" | "muted";
  className?: string;
};

type ToolHeaderBodyProps = Omit<ToolHeaderProps, "className"> & {
  open: boolean;
};

function getStatusIndicator(
  status: ToolUIPart["state"],
  label: string,
  layout: "card" | "activity",
): ReactElement {
  const icons = {
    "input-streaming": null,
    "input-available": null,
    "approval-requested": (
      <ClockIcon className="size-3 animate-pulse motion-reduce:animate-none" />
    ),
    "approval-responded": null,
    "output-available": <CheckCircleIcon className="size-3" />,
    "output-error": <XCircleIcon className="size-3 text-destructive" />,
    "output-denied": <XCircleIcon className="size-3 text-destructive" />,
  } as const;
  const semanticStatusStyles: Record<ToolUIPart["state"], string> = {
    "input-streaming": "text-muted-foreground",
    "input-available": "text-primary/80",
    "approval-requested": "text-primary/80",
    "approval-responded": "text-primary/80",
    "output-available": "text-emerald-600/75 dark:text-emerald-400/75",
    "output-error": "text-destructive/80",
    "output-denied": "text-destructive/80",
  };

  if (layout === "activity") {
    return (
      <span
        className={cn(
          "flex h-4 shrink-0 items-center gap-1 text-[11px]",
          semanticStatusStyles[status],
          ["input-streaming", "input-available", "approval-responded"].includes(
            status,
          ) && "animate-pulse motion-reduce:animate-none",
        )}
      >
        {icons[status]}
        {label}
      </span>
    );
  }

  return (
    <Badge
      className={cn(
        "h-5 shrink-0 gap-1 rounded-full px-1.5 font-normal text-[11px]",
        semanticStatusStyles[status],
        ["input-streaming", "input-available", "approval-responded"].includes(
          status,
        ) && "animate-pulse motion-reduce:animate-none",
      )}
      variant="secondary"
    >
      {icons[status]}
      {label}
    </Badge>
  );
}

function ToolHeaderBody({
  open,
  title,
  showIcon = true,
  toolName,
  type,
  state,
  layout = "card",
  shimmerTitle = false,
  titleTone = "default",
}: ToolHeaderBodyProps): ReactElement {
  const { i18n } = useLingui();
  const statusLabels: Record<ToolUIPart["state"], string> = {
    "input-streaming": i18n._(msg`Preparing`),
    "input-available": i18n._(msg`Running`),
    "approval-requested": i18n._(msg`Approval requested`),
    "approval-responded": i18n._(msg`Approval responded`),
    "output-available": i18n._(msg`Completed`),
    "output-error": i18n._(msg`Error`),
    "output-denied": i18n._(msg`Denied`),
  };
  const resolvedToolName =
    toolName ?? (type === "dynamic-tool" ? "tool" : type.slice(5));
  const displayTitle = getToolDisplayMeta(resolvedToolName).title;
  const resolvedTitle = title ?? displayTitle;

  return (
    <>
      <div
        className={cn(
          "flex min-w-0 items-center gap-1.5",
          layout === "activity" ? "max-w-full" : "flex-1",
        )}
      >
        {showIcon ? (
          <WrenchIcon className="size-3.5 shrink-0 text-muted-foreground" />
        ) : null}
        <span
          className={cn(
            "flex h-5 min-w-0 items-center overflow-hidden font-medium",
            "text-[13px]",
            layout === "activity" ? "max-w-56" : "flex-1",
            titleTone === "muted"
              ? "text-muted-foreground/60"
              : "text-foreground/90",
          )}
        >
          {shimmerTitle ? (
            <Shimmer
              as="span"
              className="max-w-full truncate leading-5 opacity-60"
              duration={1.4}
            >
              {resolvedTitle}
            </Shimmer>
          ) : (
            <span className="inline-block max-w-full truncate leading-5">
              {resolvedTitle}
            </span>
          )}
        </span>
        {getStatusIndicator(state, statusLabels[state], layout)}
      </div>
      <ChevronRightIcon
        className={cn(
          "shrink-0 text-muted-foreground transition-transform",
          open ? "rotate-90" : "rotate-0",
          layout === "activity" ? "size-3" : "size-3.5",
        )}
      />
    </>
  );
}

function getToolHeaderClassName(
  layout: NonNullable<ToolHeaderProps["layout"]>,
  className: string | undefined,
): string {
  return cn(
    "flex w-full items-center text-left",
    layout === "activity"
      ? "justify-start gap-1.5"
      : "justify-between gap-2 p-2.5",
    className,
  );
}

function ToolHeaderComponent({
  className,
  layout = "card",
  ...props
}: ToolHeaderProps): ReactElement {
  const open = useToolOpen();

  return (
    <CollapsibleTrigger
      className={getToolHeaderClassName(layout, className)}
      data-preserve-chat-scroll-on-expand=""
    >
      <ToolHeaderBody {...props} layout={layout} open={open} />
    </CollapsibleTrigger>
  );
}

export const ToolHeader = memo(
  ToolHeaderComponent,
  (prev, next) =>
    prev.className === next.className &&
    prev.title === next.title &&
    prev.showIcon === next.showIcon &&
    prev.toolName === next.toolName &&
    prev.type === next.type &&
    prev.state === next.state &&
    prev.layout === next.layout &&
    prev.shimmerTitle === next.shimmerTitle &&
    prev.titleTone === next.titleTone,
);

export type ToolDisclosureButtonProps = ToolHeaderProps & {
  controlsId: string;
  open: boolean;
  onBlur?: FocusEventHandler<HTMLButtonElement>;
  onFocus?: FocusEventHandler<HTMLButtonElement>;
  onOpenChange: (open: boolean) => void;
};

/**
 * Activity の多数の tool 行で Radix tree を作らず、同じ見た目の開閉ボタンだけを描く。
 */
export const ToolDisclosureButton = memo(
  function ToolDisclosureButton({
    className,
    controlsId,
    layout = "card",
    onBlur,
    onFocus,
    onOpenChange,
    open,
    ...props
  }: ToolDisclosureButtonProps): ReactElement {
    return (
      <button
        aria-controls={open ? controlsId : undefined}
        aria-expanded={open}
        className={getToolHeaderClassName(layout, className)}
        data-preserve-chat-scroll-on-expand=""
        data-slot="collapsible-trigger"
        onBlur={onBlur}
        onClick={() => onOpenChange(!open)}
        onFocus={onFocus}
        type="button"
      >
        <ToolHeaderBody {...props} layout={layout} open={open} />
      </button>
    );
  },
  (previous, next) =>
    previous.open === next.open &&
    previous.controlsId === next.controlsId &&
    previous.onBlur === next.onBlur &&
    previous.onFocus === next.onFocus &&
    previous.onOpenChange === next.onOpenChange &&
    previous.className === next.className &&
    previous.title === next.title &&
    previous.showIcon === next.showIcon &&
    previous.toolName === next.toolName &&
    previous.type === next.type &&
    previous.state === next.state &&
    previous.layout === next.layout &&
    previous.shimmerTitle === next.shimmerTitle &&
    previous.titleTone === next.titleTone,
);

export type ToolContentProps = ComponentProps<typeof CollapsibleContent> & {
  defer?: boolean;
};

function useDeferredRender(isOpen: boolean): boolean {
  const [shouldRender, setShouldRender] = useState(isOpen);

  useEffect(() => {
    if (!isOpen) {
      setShouldRender(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setShouldRender(true);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isOpen]);

  return shouldRender;
}

function ToolContentComponent({
  className,
  children,
  defer = false,
  ...props
}: ToolContentProps): ReactElement {
  const { i18n } = useLingui();
  const isOpen = useToolOpen();
  const deferredRenderReady = useDeferredRender(isOpen && defer);
  // 完了済み tool は初回 frame から内容を描画し、Loading との二段階 reflow を避ける。
  const shouldRender = isOpen && (!defer || deferredRenderReady);

  let content: ReactNode = null;
  if (shouldRender) {
    content = children;
  } else if (isOpen) {
    content = (
      <div className="p-3 text-muted-foreground text-[12px]">
        {i18n._(msg`Loading...`)}
      </div>
    );
  }

  return (
    <CollapsibleContent
      className={cn(
        "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-popover-foreground outline-none data-[state=closed]:animate-out data-[state=open]:animate-in",
        className,
      )}
      {...props}
    >
      {content}
    </CollapsibleContent>
  );
}

export const ToolContent = memo(
  ToolContentComponent,
  (prev, next) =>
    prev.className === next.className &&
    prev.children === next.children &&
    prev.defer === next.defer,
);

export type ToolInputProps = ComponentProps<"div"> & {
  input: ToolUIPart["input"];
  density?: "default" | "compact";
};

function ToolInputComponent({
  className,
  input,
  density = "default",
  ...props
}: ToolInputProps): ReactElement {
  const { i18n } = useLingui();
  const serializedInput = useMemo(
    () => JSON.stringify(input, null, 2),
    [input],
  );

  const isCompact = density === "compact";

  return (
    <div
      className={cn(
        "overflow-hidden",
        isCompact ? "space-y-1 p-1.5" : "space-y-1.5 p-2.5",
        className,
      )}
      {...props}
    >
      <div className="flex min-h-4 items-center justify-between">
        <h4
          className={cn(
            "font-medium text-muted-foreground uppercase",
            isCompact
              ? "text-[10px] leading-4 tracking-[0.04em]"
              : "text-xs tracking-wide",
          )}
        >
          {i18n._(msg`Parameters`)}
        </h4>
        <CopyToClipboardButton
          className={cn(
            isCompact ? "size-4 [&_svg]:size-2.5" : "size-5 [&_svg]:size-3",
          )}
          size="icon-sm"
          textToCopy={serializedInput}
        />
      </div>
      <div
        className={cn("bg-muted/50", isCompact ? "rounded-sm" : "rounded-md")}
      >
        <CodeBlock code={serializedInput} density={density} language="json" />
      </div>
    </div>
  );
}

export const ToolInput = memo(
  ToolInputComponent,
  (prev, next) =>
    prev.className === next.className &&
    prev.input === next.input &&
    prev.density === next.density,
);

export type ToolOutputProps = ComponentProps<"div"> & {
  output: ToolUIPart["output"];
  errorText: ToolUIPart["errorText"];
  density?: "default" | "compact";
};

const TOOL_OUTPUT_VIRTUALIZE_LINE_COUNT = 80;
const TOOL_OUTPUT_VIRTUALIZE_CHAR_COUNT = 8000;

function ToolOutputComponent({
  className,
  output,
  errorText,
  density = "default",
  ...props
}: ToolOutputProps): ReactElement | null {
  const { i18n } = useLingui();
  const hasContent = Boolean(output || errorText);
  const isCompact = density === "compact";

  const serializedOutput = useMemo(() => {
    if (typeof output === "string") {
      return output;
    }

    if (
      typeof output === "object" &&
      output !== null &&
      !isValidElement(output)
    ) {
      return JSON.stringify(output, null, 2);
    }

    return null;
  }, [output]);

  const outputLines = useMemo(() => {
    if (!serializedOutput) {
      return [];
    }
    return serializedOutput.split(/\r?\n/);
  }, [serializedOutput]);

  const shouldVirtualize =
    serializedOutput !== null &&
    (serializedOutput.length > TOOL_OUTPUT_VIRTUALIZE_CHAR_COUNT ||
      outputLines.length > TOOL_OUTPUT_VIRTUALIZE_LINE_COUNT);

  let content: ReactNode = <div>{output as ReactNode}</div>;

  if (serializedOutput !== null) {
    if (shouldVirtualize) {
      content = (
        <VirtualizedCodeBlock
          lines={outputLines}
          language="json"
          maxHeight={isCompact ? 192 : undefined}
          minHeight={isCompact ? 80 : undefined}
          lineHeight={isCompact ? 16 : undefined}
        />
      );
    } else {
      content = (
        <CodeBlock code={serializedOutput} density={density} language="json" />
      );
    }
  }

  if (!hasContent) {
    return null;
  }

  return (
    <div
      className={cn(
        isCompact ? "space-y-1 p-1.5" : "space-y-1.5 p-2.5",
        className,
      )}
      {...props}
    >
      <div className="flex min-h-4 items-center justify-between">
        <h4
          className={cn(
            "font-medium text-muted-foreground uppercase",
            isCompact
              ? "text-[10px] leading-4 tracking-[0.04em]"
              : "text-xs tracking-wide",
          )}
        >
          {errorText ? i18n._(msg`Error`) : i18n._(msg`Result`)}
        </h4>
        {serializedOutput !== null ? (
          <CopyToClipboardButton
            className={cn(
              isCompact ? "size-4 [&_svg]:size-2.5" : "size-5 [&_svg]:size-3",
            )}
            size="icon-sm"
            textToCopy={serializedOutput}
          />
        ) : null}
      </div>
      <div
        className={cn(
          "rounded-md text-xs [&_table]:w-full",
          isCompact && "rounded-sm text-[11px]",
          isCompact && serializedOutput === null && "max-h-48 overflow-auto",
          errorText
            ? "bg-destructive/10 text-destructive"
            : "bg-muted/50 text-foreground",
          shouldVirtualize ? "overflow-hidden" : "overflow-x-auto",
        )}
      >
        {errorText && <div>{errorText}</div>}
        {content}
      </div>
    </div>
  );
}

export const ToolOutput = memo(ToolOutputComponent, (prev, next) => {
  if (prev.className !== next.className) return false;
  if (prev.errorText !== next.errorText) return false;
  if (prev.density !== next.density) return false;

  const a = prev.output;
  const b = next.output;
  if (a === b) return true;

  if (typeof a === "string" && typeof b === "string") return a === b;

  if (
    typeof a === "object" &&
    a !== null &&
    typeof b === "object" &&
    b !== null
  ) {
    if (isValidElement(a) || isValidElement(b)) return a === b;
    return false;
  }

  return false;
});
